/**
 * Global axios + toast guards for API outages (502/503/504 / network).
 * Install once at app bootstrap (index.js), after transient-retry so we only
 * mark outage when retries are exhausted.
 */
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  noteApiSuccess,
  reportErrorIfOutage,
  isApiOutage,
} from '../services/apiAvailability';

let axiosInstalled = false;
let toastInstalled = false;

function shouldIgnoreUrl(url) {
  const u = String(url || '');
  return u.includes('/api/health') || u.includes('/ready') || u.includes('/health');
}

export function installAxiosOutageGuard() {
  if (axiosInstalled) return;
  axiosInstalled = true;

  axios.interceptors.response.use(
    (response) => {
      noteApiSuccess();
      return response;
    },
    (error) => {
      if (!shouldIgnoreUrl(error?.config?.url)) {
        reportErrorIfOutage(error);
      }
      return Promise.reject(error);
    }
  );
}

/**
 * Suppress toast.error while the API is down so pages that still call
 * toast.error on fetch failure don't spam red popups.
 */
export function installToastOutageGuard() {
  if (toastInstalled) return;
  toastInstalled = true;

  const originalError = toast.error.bind(toast);
  toast.error = (content, options) => {
    if (isApiOutage()) {
      return undefined;
    }
    return originalError(content, options);
  };
}

/** Attach the same success/outage hooks to a custom axios instance (e.g. apiService). */
export function attachOutageInterceptors(instance) {
  if (!instance?.interceptors?.response || instance.__coursoloOutageAttached) return;
  instance.__coursoloOutageAttached = true;

  instance.interceptors.response.use(
    (response) => {
      noteApiSuccess();
      return response;
    },
    (error) => {
      if (!shouldIgnoreUrl(error?.config?.url)) {
        reportErrorIfOutage(error);
      }
      return Promise.reject(error);
    }
  );
}

export default installAxiosOutageGuard;
